/**
 * Action points setup
 * Handles initial setup of action points for a scene
 */

import type { SceneData, ChoiceCondition } from '../types'
import { createActionPoint } from './action-points'
import { filterBubblesByCondition } from './bubble-filter'

export interface ActionPointsSetupContext {
  imageContainer: HTMLDivElement | null
  data: SceneData
  clickedActionPointIndices: Set<number>
  onCreateActionPoint: (xPercent: number, yPercent: number, bubbleIndex: number, actionPointIndex: number) => void
  onEvaluateCondition?: (condition: ChoiceCondition) => boolean
}

/**
 * Setup initial action points for a scene
 */
export function setupActionPoints(context: ActionPointsSetupContext): void {
  if (!context.imageContainer) return

  // Wait for image dimensions to be available (not full download)
  const setup = () => {
    const img = context.imageContainer?.querySelector('img') as HTMLImageElement | null
    if (img && !(img.naturalWidth > 0)) {
      setTimeout(setup, 50)
      return
    }

    requestAnimationFrame(() => {
      // Filter bubbles based on conditions
      const filteredBubbles = filterBubblesByCondition(
        context.data.speechBubbles,
        context.onEvaluateCondition
      )
      if (filteredBubbles.length === 0) return

      // Check saved state to see if action points were already clicked
      // Note: This is handled in restoreState now, but we keep this for initial setup
      // The restoreState method will be called after scenes are loaded

      // Show first action point if not already clicked
      if (!context.clickedActionPointIndices.has(0)) {
        const firstBubble = filteredBubbles[0]
        if (!firstBubble.actionPoint || !firstBubble.actionPoint.position) {
          console.error(
            `Scene ${context.data.id}: speechBubble "${firstBubble.id}" is missing required "actionPoint.position"`,
          )
          return
        }
        const actionPointPos = firstBubble.actionPoint.position
        context.onCreateActionPoint(actionPointPos.x, actionPointPos.y, 0, 0)
      }
    })
  }

  requestAnimationFrame(setup)
}
